// CartContext — estado global del carrito de la tienda v5.
//
// Decisión (Sub-fase 4.6, opción A confirmada por Nico): NO inventa store nuevo. Reusa la MISMA
// clave localStorage 'limpack_carrito_v1' y la MISMA forma de ítem que la tienda vieja
// (Tienda.jsx / TiendaCatalogo.jsx / CarritoFlotante.jsx / Cuenta.jsx) y que consume el backend
// crear-pedido-publico. Así v5 y la tienda vieja comparten carrito y el merge a main no rompe nada.
//
// Forma del ítem (single source of truth del proyecto):
//   { sku, nombre, presentacion, precio_unit: Number, cantidad: Number }
//
// Reactividad cross-instancia: cada mutación persiste + dispara el evento window 'lp:carrito-changed'
// (mismo que la tienda vieja). El provider escucha ese evento + 'storage' (otras pestañas) para
// resincronizar. El guard por igualdad JSON corta el loop con nuestras propias escrituras.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { trackEvent } from './metaPixel';

const STORAGE_CARRITO = 'limpack_carrito_v1';
export const ENVIO_GRATIS_DESDE = 1000; // $1.000 — umbral de envío gratis (compra suelta)

const CartCtx = createContext(null);

function leerCarrito() {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_CARRITO));
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

// Persiste + avisa a todas las instancias (tienda vieja incluida: CarritoFlotante/Cuenta escuchan lo mismo).
function persistir(items) {
  try { localStorage.setItem(STORAGE_CARRITO, JSON.stringify(items)); } catch { /* modo privado / quota */ }
  window.dispatchEvent(new Event('lp:carrito-changed'));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(leerCarrito);
  const montado = useRef(false);

  // Persistir + avisar cuando cambia el carrito (mismo patrón que la tienda vieja: efecto sobre el
  // estado, NO dentro del updater → sin setState re-entrante). Salta el mount para no re-escribir al cargar.
  useEffect(() => {
    if (!montado.current) { montado.current = true; return; }
    persistir(items);
  }, [items]);

  // Espejo del carrito en la cuenta (carritos_cliente) cuando hay sesión. Sirve para ver carritos
  // abandonados y recuperar la venta (caso Giorgina, 26-jul: se registró, no compró, y no teníamos
  // ni idea de qué había armado). El localStorage sigue siendo la fuente para el front; esto es
  // best-effort y NUNCA bloquea ni rompe el carrito si falla (visitante → la RPC no hace nada).
  useEffect(() => {
    if (!montado.current) return;
    const t = setTimeout(() => {
      supabase.rpc('guardar_mi_carrito', { p_items: items, p_paso: 'catalogo' })
        .then(({ error }) => { if (error) console.warn('[Cart] no se pudo espejar el carrito:', error.message); });
    }, 1200); // debounce: no una escritura por cada +/− de cantidad
    return () => clearTimeout(t);
  }, [items]);

  // Sync entrante: 'storage' (otra pestaña) + 'lp:carrito-changed' (mismo tab, p.ej. la tienda vieja).
  // El guard por igualdad JSON evita re-render y corta el eco con nuestra propia escritura.
  useEffect(() => {
    function recargar() {
      setItems((prev) => {
        const next = leerCarrito();
        return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
      });
    }
    function onStorage(e) { if (!e.key || e.key === STORAGE_CARRITO) recargar(); }
    window.addEventListener('storage', onStorage);
    window.addEventListener('lp:carrito-changed', recargar);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('lp:carrito-changed', recargar);
    };
  }, []);

  // Merge cross-domain (limpack.uy → tienda): si la URL trae ?merge_cart={base64(JSON items)}
  // (lo manda el "Ir a pagar" de la landing vanilla limpack.uy), mergeamos con el carrito local
  // (unión por sku, sumando cantidades), guardamos y limpiamos el param para no re-mergear al refrescar.
  // El base64 se decodifica UTF-8-safe (nombres con acentos). Corre 1 vez al montar.
  useEffect(() => {
    let raw;
    try { raw = new URLSearchParams(window.location.search).get('merge_cart'); } catch { raw = null; }
    if (raw) {
      try {
        const incoming = JSON.parse(decodeURIComponent(escape(atob(raw))));
        if (Array.isArray(incoming) && incoming.length) {
          setItems((prev) => {
            const map = new Map(prev.map((it) => [it.sku, { ...it }]));
            for (const it of incoming) {
              if (!it || !it.sku) continue;
              const ex = map.get(it.sku);
              if (ex) ex.cantidad = (ex.cantidad || 0) + (Number(it.cantidad) || 0);
              else map.set(it.sku, {
                sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
                precio_unit: Number(it.precio_unit ?? it.precio ?? 0), cantidad: Number(it.cantidad) || 1,
              });
            }
            return [...map.values()].filter((it) => it.cantidad > 0);
          });
        }
      } catch (e) { console.warn('[Cart] merge_cart inválido:', e?.message || e); }
      // Limpiar el param del URL (aunque el parse haya fallado) para no reintentar en cada refresh.
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('merge_cart');
        window.history.replaceState({}, '', u.pathname + u.search + u.hash);
      } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = useCallback((p, cantidad = 1) => {
    // Meta Pixel: AddToCart con el SKU como content_id (alimenta los anuncios de catálogo).
    const precioUnit = Number(p.precio_unit ?? p.precio ?? p.precio_publico) || 0;
    trackEvent('AddToCart', {
      content_ids: [p.sku],
      content_type: 'product',
      value: precioUnit * cantidad,
      currency: 'UYU',
    });
    setItems((prev) => {
      const ix = prev.findIndex((it) => it.sku === p.sku);
      if (ix >= 0) {
        const cp = [...prev];
        cp[ix] = { ...cp[ix], cantidad: cp[ix].cantidad + cantidad };
        return cp;
      }
      // Forma EXACTA que consume el backend (crear-pedido-publico) y la tienda vieja.
      return [...prev, {
        sku: p.sku,
        nombre: p.nombre,
        presentacion: p.presentacion,
        precio_unit: Number(p.precio_unit ?? p.precio ?? p.precio_publico),
        cantidad,
      }];
    });
  }, []);

  const removeItem = useCallback((sku) => {
    setItems((prev) => prev.filter((it) => it.sku !== sku));
  }, []);

  // Cantidad ABSOLUTA (≤0 saca el ítem).
  const updateQty = useCallback((sku, cantidad) => {
    setItems((prev) => prev
      .map((it) => (it.sku === sku ? { ...it, cantidad } : it))
      .filter((it) => it.cantidad > 0));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { itemsCount, subtotal } = useMemo(() => ({
    itemsCount: items.reduce((s, it) => s + (it.cantidad || 0), 0),
    subtotal: items.reduce((s, it) => s + (it.precio_unit || 0) * (it.cantidad || 0), 0),
  }), [items]);

  // Cuánto falta para el envío gratis (0 = ya calificás). El MiniBar lo usa para "Faltan $X".
  const faltaEnvioGratis = Math.max(0, ENVIO_GRATIS_DESDE - subtotal);

  const value = useMemo(() => ({
    items, addItem, removeItem, updateQty, clear,
    itemsCount, subtotal, faltaEnvioGratis,
  }), [items, addItem, removeItem, updateQty, clear, itemsCount, subtotal, faltaEnvioGratis]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error('useCart debe usarse dentro de <CartProvider>');
  return ctx;
}
